import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { platformKey } from './terminalProfiles';

/**
 * The Terminal's default ("root") working directory — `prefs.defaultCwd` in
 * the per-account, per-device terminal config (never synced: paths are per
 * machine). New tabs start here; splits follow their pane; a profile with
 * its own folder (fixed / home / ask) overrides it.
 */

/**
 * The same shape rule as Rust's `terminal/cwd.rs` `is_local_absolute` +
 * `is_network_or_device_path`: on Windows only a drive-letter path (`C:\…`),
 * elsewhere only `/…`; never UNC / device paths. Rust re-checks every launch
 * (and falls back to home for a missing folder) — this only drives the
 * settings warning and keeps an unusable value out of pane specs.
 */
export function isLocalAbsolutePath(p, windows = platformKey() === 'win') {
  if (typeof p !== 'string') return false;
  const s = p.trimStart();
  const sep = (c) => c === '\\' || c === '/';
  if (s.length >= 2 && sep(s[0]) && sep(s[1])) return false;
  if (windows) return /^[A-Za-z]:[\\/]/.test(s);
  return s.startsWith('/');
}

/** The usable root folder, or undefined (none set / not a local absolute path). */
export function getDefaultCwd() {
  const raw = useTerminalConfigStore.getState().prefs?.defaultCwd;
  const p = typeof raw === 'string' ? raw.trim() : '';
  return p && isLocalAbsolutePath(p) ? p : undefined;
}

/** Extra spec for a new plain-shell tab: `{ cwd }` in the root folder, or `{}` (home). */
export function newTabCwd() {
  const cwd = getDefaultCwd();
  return cwd ? { cwd } : {};
}
